import type { TenantDestinationDTO } from '@/shared/api/mappers/tenant-mapper'
import type {
  ApiResponse,
  ApiResponseEnvelope,
  PaginatedResponse,
} from '@/shared/api/types/api-response'
import type {
  CreateTenantDestinationPayload,
  ITenantDestination,
  TenantDestinationListParams,
  TenantDestinationListResponse,
  UpdateTenantDestinationPayload,
} from '@/shared/api/types/tenant-destination-types'
import { GW, toQueryString } from '@/shared/api/endpoints'
import { mapTenantDestinationDto } from '@/shared/api/mappers/tenant-mapper'
import { buildLanguageHeaderConfig } from '@/shared/api/utils/language-header'
import { buildTenantHeaderConfig } from '@/shared/api/utils/tenant-header'
import { unwrapApiEnvelope } from '@/shared/api/utils/unwrapapi'
import { request } from '@/shared/libs/request'

const TENANT_DESTINATIONS_PATH = 'admin/tenant-destinations'

interface TenantScopedRequestOptions {
  tenantId?: string
}

interface TenantDestinationListOptions {
  languageCode?: string
}

export async function listTenantDestinations(
  params: TenantDestinationListParams,
  options: TenantDestinationListOptions = {},
): Promise<TenantDestinationListResponse> {
  const qs = toQueryString({
    current: params.current,
    pageSize: params.pageSize,
    ...(params.isDeleted !== undefined && params.isDeleted !== null
      ? { isDeleted: params.isDeleted }
      : {}),
    ...(params.isActive !== undefined && params.isActive !== null
      ? { isActive: params.isActive }
      : {}),
    ...(params.destinationId ? { destinationId: params.destinationId } : {}),
    ...(params.destinationType ? { destinationType: params.destinationType } : {}),
    ...(params.countryId ? { countryId: params.countryId } : {}),
    ...(params.provinceId ? { provinceId: params.provinceId } : {}),
  })
  const languageCode = options.languageCode ?? params.languageCode
  const languageHeaders = buildLanguageHeaderConfig(languageCode).headers
  const tenantHeaders = buildTenantHeaderConfig(params.tenantId).headers
  const headers = { ...languageHeaders, ...tenantHeaders }

  const response = await request.get<ApiResponseEnvelope<PaginatedResponse<TenantDestinationDTO>>>(
    GW.packaging(`${TENANT_DESTINATIONS_PATH}?${qs}`),
    { ...(Object.keys(headers).length ? { headers } : {}) },
  )
  const data = unwrapApiEnvelope(response.data)
  return {
    data: {
      data: data.data.map(mapTenantDestinationDto),
      total: data.total,
      current: data.current,
      pageSize: data.pageSize,
      keyword: data.keyword ?? null,
    },
  }
}

export async function getTenantDestinationById(
  id: string,
  languageCode?: string,
): Promise<ApiResponse<ITenantDestination>> {
  const response = await request.get<ApiResponseEnvelope<TenantDestinationDTO>>(
    GW.packaging(`${TENANT_DESTINATIONS_PATH}/${id}`),
    buildLanguageHeaderConfig(languageCode),
  )
  return { data: mapTenantDestinationDto(unwrapApiEnvelope(response.data)) }
}

export async function createTenantDestination(
  payload: CreateTenantDestinationPayload,
  options: TenantScopedRequestOptions = {},
): Promise<ApiResponse<ITenantDestination>> {
  const response = await request.post<ApiResponseEnvelope<TenantDestinationDTO>>(
    GW.packaging(TENANT_DESTINATIONS_PATH),
    payload,
    buildTenantHeaderConfig(options.tenantId),
  )
  return {
    data: mapTenantDestinationDto(unwrapApiEnvelope(response.data)),
    message: 'Tenant destination created successfully',
  }
}

export async function updateTenantDestination(
  id: string,
  payload: UpdateTenantDestinationPayload,
  options: TenantScopedRequestOptions = {},
): Promise<ApiResponse<ITenantDestination>> {
  const response = await request.patch<ApiResponseEnvelope<TenantDestinationDTO>>(
    GW.packaging(`${TENANT_DESTINATIONS_PATH}/${id}`),
    payload,
    buildTenantHeaderConfig(options.tenantId),
  )
  return {
    data: mapTenantDestinationDto(unwrapApiEnvelope(response.data)),
    message: 'Tenant destination updated successfully',
  }
}

export async function deleteTenantDestination(
  id: string,
  options: TenantScopedRequestOptions = {},
): Promise<ApiResponse<null>> {
  await request.delete(
    GW.packaging(`${TENANT_DESTINATIONS_PATH}/${id}`),
    buildTenantHeaderConfig(options.tenantId),
  )
  return { data: null, message: 'Tenant destination deleted successfully' }
}
